export const subjects = {
     maths: {
          label: "Mathematics",
          name: "Maths",
          lcolor: "#e5c2f0",
          dcolor: "#7c2c99",
          description:
               "Students will develop strong numerical and problem-solving skills through structured, progressive lessons that build accuracy and confidence in maths. They will learn to work with a range of topics including arithmetic, algebra, geometry, and data handling, while improving their ability to apply logical thinking and working methods to exam-style questions. By mastering core mathematical concepts and practising effective problem-solving strategies, students gain the skills and confidence needed to succeed in 11+ maths assessments.",
     },

     english: {
          label: "English",
          name: "English",
          lcolor: "#fdd765",
          dcolor: "#bf9000",
          description:
               "Students will build strong reading, writing, and comprehension skills through structured lessons that focus on clarity, accuracy, and expression. They will develop a richer vocabulary, improved grammar, and the ability to analyse texts thoughtfully and respond with confidence. By strengthening core literacy skills and learning effective exam techniques, students gain the knowledge and self-assurance needed to succeed in 11+ English assessments.",
     },

     vr: {
          label: "Verbal Reasoning",
          name: "VR",
          lcolor: "#92d050",
          dcolor: "#548235",
          description:
               "Students will develop strong logical thinking and problem-solving skills through clear, structured lessons that build confidence in tackling verbal reasoning challenges. They will learn how to identify patterns, understand word relationships, solve codes, and interpret sequences with accuracy and speed. By strengthening vocabulary, analytical thinking, and attention to detail, students gain the skills and strategies needed to approach 11+ verbal reasoning questions with confidence and precision.",
     },

     nvr: {
          label: "Non-Verbal Reasoning",
          name: "NVR",
          lcolor: "#ffc06d",
          dcolor: "#ff8a3b",
          description:
               "Students will strengthen their visual and spatial reasoning skills through clear, step-by-step lessons designed to build accuracy and confidence. They will learn how to identify patterns, analyse shapes, recognise sequences, and solve complex visual problems with speed and precision. By developing logical thinking, attention to detail, and problem-solving strategies, students gain the confidence and techniques needed to approach 11+ non-verbal reasoning questions methodically and successfully.",
     },
};

export const variants = {
     g2: {
          record: "Small Group Boost",
          population: "3-1 Classes",
          freq: "2 lessons a week",
     },
     g1: {
          record: "Small Group Tuition",
          population: "3-1 Classes",
          freq: "1 lesson a week",
     },
     o2: {
          record: "1-1 Tuition",
          population: "1-1 Classes",
          freq: "2 lessons a week",
     },
     o1: {
          record: "1-1 Intense",
          population: "1-1 Classes",
          freq: "1 lesson a week",
     },
};

function createPlan(
     baseId,
     web,
     name,
     subjectsList,
     blend,
     pricing,
     allowedVariants = null
) {
     const permitted = allowedVariants || Object.keys(variants);

     return Object.fromEntries(
          permitted.map((variantKey) => {
               const data = variants[variantKey];

               return [
                    `${baseId}-${variantKey}`,
                    {
                         id: `${baseId}-${variantKey}`,
                         web,
                         name,
                         subjects: subjectsList,
                         blend,
                         cost: pricing[variantKey],
                         ...data,
                    },
               ];
          })
     );
}

export const plans = {
     ...createPlan(
          "full11",
          "full11+course",
          "Full 11+ course",
          ["maths", "english", "vr", "nvr"],
          "Teaching Maths, English, Verbal Reasoning and Non-Verbal Reasoning together gives students a complete, well-rounded foundation for the 11+. They learn to interpret language carefully, think logically with numbers, recognise patterns quickly, and apply visual reasoning with confidence—key skills that strengthen accuracy, speed, and performance across the entire exam.",
          {
               o2: "£37 per lesson",
               g2: "£25 per lesson",
          },
          ["g2", "o2"]
     ),

     ...createPlan(
          "core11",
          "11+core",
          "11+ Core",
          ["maths", "english"],
          "Teaching Maths and English together gives students a strong, balanced foundation for the 11+. They learn to interpret questions accurately, think logically, and express answers clearly—key skills that boost confidence and performance across the exam.",
          {
               o1: "£40 per lesson",
               o2: "£34 per lesson",
               g1: "£26 per lesson",
               g2: "£23 per lesson",
          },
          ["g2", "g1", "o2", "o1"]
     ),



     ...createPlan(
          "reason11",
          "11+reasoning",
          "11+ Reasoning",
          ["vr", "nvr"],
          "Teaching Verbal Reasoning and Non-Verbal Reasoning together sharpens students’ pattern recognition and logical thinking for the 11+. They learn to analyse language carefully, identify sequences and relationships quickly, and apply structured reasoning to both words and visuals—key skills that strengthen accuracy, speed, and confidence across the exam.",
          {
               o1: "£40 per lesson",
               o2: "£34 per lesson",
               g1: "£26 per lesson",
               g2: "£23 per lesson",
          },
          ["g2", "g1", "o2", "o1"]
     ),

     ...createPlan(
          "engvr",
          "englishvr",
          "English & VR",
          ["english", "vr"],
          "Teaching Verbal Reasoning and English together strengthens the language skills at the heart of the 11+. Students learn to interpret meaning precisely, expand their vocabulary, recognise patterns within words, and think critically about language—key skills that improve accuracy, speed, and confidence across the exam.",
          {
               o1: "£40 per lesson",
               o2: "£34 per lesson",
               g1: "£26 per lesson",
               g2: "£23 per lesson",
          },
          ["g2", "g1", "o2", "o1"]
     ),

     ...createPlan(
          "mathsnvr",
          "mathsnvr",
          "Maths & NVR",
          ["maths", "nvr"],
          "Teaching Maths and Non-Verbal Reasoning together builds strong logical and analytical thinking for the 11+. Students learn to approach problems systematically, recognise patterns quickly, and apply visual and numerical reasoning with precision—key skills that improve accuracy, speed, and confidence across the exam.",
          {
               o1: "£40 per lesson",
               o2: "£34 per lesson",
               g1: "£26 per lesson",
               g2: "£23 per lesson",
          },
          ["g2", "g1", "o2", "o1"]
     ),

     ...createPlan(
          "english",
          "englishfocus",
          "English Focus",
          ["english"],
          "",
          {
               o1: "£35 per lesson",
               o2: "£29 per lesson",
               g1: "£23 per lesson",
               g2: "£20 per lesson",
          },
          ["g2", "g1", "o2", "o1"]
     ),

     ...createPlan(
          "maths",
          "mathsfocus",
          "Maths Focus",
          ["maths"],
          "",
          {
               o1: "£40 per lesson",
               o2: "£34 per lesson",
               g1: "£26 per lesson",
               g2: "£23 per lesson",
          },
          ["g2", "g1", "o2", "o1"]
     ),

     ...createPlan(
          "vr",
          "vrfocus",
          "VR Focus",
          ["vr"],
          "",
          {
               o1: "£35 per lesson",
               o2: "£29 per lesson",
               g1: "£23 per lesson",
               g2: "£20 per lesson",
          },
          ["g2", "g1", "o2", "o1"]
     ),

     ...createPlan(
          "nvr",
          "nvrfocus",
          "NVR Focus",
          ["nvr"],
          "",
          {
               o1: "£35 per lesson",
               o2: "£29 per lesson",
               g1: "£23 per lesson",
               g2: "£20 per lesson",
          },
          ["g2", "g1", "o2", "o1"]
     ),

     ...createPlan(
          "trio",
          "mathsenglishvr",
          "Maths, English & VR",
          ["maths", "english", "vr"],
          "Teaching Maths, English and Verbal Reasoning together gives students a strong, well-rounded foundation for the 11+. They learn to interpret questions precisely, apply logical thinking, and recognise patterns within language—key skills that improve accuracy, speed, and confidence across the exam.",
          {
               o2: "£37 per lesson",
               g2: "£25 per lesson",
          },
          ["g2", "o2"]
     )
};

export const plandes = {
     full11:
          "This plan is designed for students preparing for 11+ entrance exams that include English, maths, verbal reasoning, and non-verbal reasoning, as well as high-achieving primary school students who want to extend their learning and tackle more challenging material. It is also ideal for students who want to build a strong foundation in core academic skills and gain a head start in the key concepts that will be explored further at secondary school.",

     core11:
          "This plan is designed for students whose target schools only test for English and Maths, high achieving primary school students who want a challenge, and students who want to get a heads start on the core concepts that will be further explores in secondary school.",

     reason11:
          "This course is designed for students whose target schools only test verbal and non-verbal reasoning, high-achieving primary school students who want an additional academic challenge, and students who want to build strong logical, spatial, and problem-solving skills. It is also suitable for students looking to develop confidence and familiarity with core reasoning question types while gaining a head start on the skills needed for secondary school entrance exams.",

     engvr:
          "This course is designed for students whose target schools test English and verbal reasoning, high-achieving primary school students who want to extend their learning, and students aiming to strengthen their literacy and logical thinking skills. It is also suitable for students who want to build confidence in comprehension, vocabulary, and reasoning question types while getting a head start on the core skills needed for secondary school entrance exams.",

     mathsnvr:
          "This course is designed for students whose target schools test maths and non-verbal reasoning, high-achieving primary school students who want an additional academic challenge, and students aiming to strengthen their numerical and spatial problem-solving skills. It is also suitable for students who want to build confidence in tackling mathematical and visual reasoning questions while gaining a head start on the core skills needed for secondary school entrance exams.",

     maths:
          "This course is designed for students whose target schools only test maths, high-achieving primary school students who want to extend their learning, and students who want to build strong numerical and problem-solving skills. It is also suitable for students looking to improve their confidence in core mathematical concepts while gaining a head start on the skills needed for secondary school entrance exams.",

     english:
          "This course is designed for students whose target schools only test English, high-achieving primary school students who want to extend their learning, and students aiming to strengthen their reading, writing, and comprehension skills. It is also suitable for students who want to build confidence in literacy and language-based exam questions while gaining a head start on the skills needed for secondary school entrance exams.",

     vr:
          "This course is designed for students whose target schools only test verbal reasoning, high-achieving primary school students who want an additional academic challenge, and students aiming to strengthen their logical thinking, vocabulary, and problem-solving skills. It is also suitable for students who want to build confidence in tackling verbal reasoning question types while gaining a head start on the skills needed for secondary school entrance exams.",

     nvr:
          "This course is designed for students whose target schools only test non-verbal reasoning, high-achieving primary school students who want an additional academic challenge, and students aiming to strengthen their visual and spatial problem-solving skills. It is also suitable for students who want to build confidence in tackling non-verbal reasoning question types while gaining a head start on the skills needed for secondary school entrance exams.",

     trio:
          "This course is designed for students whose target schools test maths, English, and verbal reasoning, high-achieving primary school students who want a greater academic challenge, and students aiming to strengthen their numerical, literacy, and reasoning skills. It is also suitable for students who want to build confidence across these core subjects while gaining a strong foundation for the skills needed in secondary school entrance exams.",
};